import { useState, useEffect, useMemo, useRef } from "react";
import { dirOf } from "../utils/textDir";
import { publishedNote } from "../utils/formatDate";
import { useMarketStatus } from "../utils/marketStatus";
// UI = the VISION.md role-based palette (staged: home screen only for now).
// T is still imported for the non-colour scales (radius) it owns.
import { T, UI } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { fetchEarningsCalendar } from "../utils/earningsCalendar";
import { fetchGeoEvents, computeOverlap, fetchScanStats, fetchLastScan, isOfficial } from "../utils/geoEvents";
import { fetchMarketWeek } from "../utils/calendar";
import { ConceptOfWeekLine } from "./ConceptOfWeekCard";
import UpcomingCard from "./UpcomingCard";
import { ACADEMY_ENABLED } from "../constants/features";
import OfficialBadge from "./OfficialBadge";
import { reportedHeadline } from "../utils/voice";
import Headline from "./Headline";
import DualMoney from "./DualMoney";
import { requestScroll } from "../utils/scrollTarget";
import { timeAgoMs } from "../utils/timeAgo";
import ScanStatusDot from "./ScanStatusDot";
import { useIsDesktop } from "../utils/hooks";
import Icon from "./Icon";
import LTR from "./LTR";
import InsightCard from "./InsightCard";
import MarkInviteBanner from "./MarkInviteBanner";
import PortfolioMoversCard, { indexForSession } from "./PortfolioMoversCard";
import BriefCard from "./BriefCard";
import MarketStateBadge from "./MarketStateBadge";
import { topMovers } from "../utils/portfolioMovers";
import { useExtendedHours, estimateExtended, futureFor } from "../utils/extendedHours";
import { fetchMarketData } from "../utils/marketdata";
import MarketTodayCard from "./MarketTodayCard";
import PortfolioTie from "./PortfolioTie";
import Money from "./Money";
import InfoTip from "./InfoTip";
import { fmtPct, shownDir } from "../utils/formatters";
import { dateNumeric } from "../utils/formatDate";
import { readBaseline, markVisit, buildSnapshot } from "../lib/homeVisit";
import { computeChanges, checkSnapshot, daysUntilText, NOT_COMPARABLE, NOT_COMPARABLE_WHY } from "../utils/homeChanges";
import { useTodayNews } from "../utils/todayMoves";
import { CARD_ACTION } from "./Card";

// One card, one look, for the whole screen (David, 2026-09-23). The hairline
// frame is gone: a card is a surface with a lift, not a box with an edge. The
// "secondary" tier no longer differs in how it is drawn — the label at the top
// of every card already says what kind of thing it is (CardKind.jsx), and two
// visual weights on top of that said the same thing twice, badly.
const CARD = {
  background: UI.surface.card,
  borderRadius: T.radius.xl,      // --r-xl, the spec's card step
  boxShadow: UI.shadow.cardEl,
};

const PAD = "22px 22px 24px";   // the spec's card gutter
// Navigation actions on the home screen read as text links, not boxed buttons.
// The style moved to Card.jsx when the exposure card became its own file —
// both read it from there, and nothing imports HomeTab to get it.
const ACTION_BTN = CARD_ACTION;
const WATCH_TOP = 3;
// "אירועים קרובים" looks two weeks ahead (sprint 18, stage 3 · it was 7 days).
const AHEAD_DAYS = 14;


// ─── A section, not a panel ──────────────────────────────────────────────────
// VISION: a panel only where there is a clear reason to separate. The home
// screen answers three questions; a heading and a rule are enough to say
// where one ends and the next begins, and the page reads as one page instead
// of as a stack of boxes.
function Section({ title, note, children, action, onAction, style = {} }) {
  return (
    <section style={{ paddingTop: 4, ...style }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h2 style={{ ...TYPE.heading, margin: 0 }}>{title}</h2>
        {note && <span style={{ fontSize: 12, color: UI.text.muted }}>{note}</span>}
      </div>
      {children}
      {action && (
        <button onClick={onAction} style={ACTION_BTN}>{action}<span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span></button>
      )}
    </section>
  );
}

// ─── One window of the scan, every number named (sprint 13, 2.2) ────────────
// "היום · 1 כתבות, 0 רלוונטיות" said neither what was counted nor since when.
// Each figure now carries its own label, and the window says how long it is.
function ScanStatsRow({ stats }) {
  const { scanned, events, relevant, capped, hours } = stats;
  // A capped page makes the two counts drawn from it lower bounds: "לפחות".
  const cell = (value, label, atLeast = false) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: UI.text.primary }}>
        {atLeast && <span style={{ fontSize: 12, fontWeight: 600, color: UI.text.muted }}>לפחות </span>}
        <LTR className="si-num">{value}</LTR>
      </div>
      <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.45, marginTop: 2 }}>{label}</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: UI.text.secondary, marginBottom: 8 }}>
        {hours === 24 ? <><LTR>24</LTR> השעות האחרונות</> : <><LTR>{Math.round(hours / 24)}</LTR> הימים האחרונים</>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
        {cell(scanned.toLocaleString("en-US"), "סווגו בסורק")}
        {cell(events.toLocaleString("en-US"), "סווגו כאירוע גיאופוליטי", capped)}
        {cell(relevant.toLocaleString("en-US"), "מהאירועים נוגעים לתיק", capped)}
      </div>
    </div>
  );
}

export default function HomeTab({ portfolio = [], portfolioReady = true, watchlist = [], firstName = "", cur = "USD", displayStyle = "balanced", isGuest = false, userId = null, onNavigate }) {
  const isDesktop = useIsDesktop();
  const market = useMarketStatus();
  const [geoEvents, setGeoEvents] = useState(null);   // null = loading
  const [weekRows,  setWeekRows]  = useState(null);   // null = loading
  const [scanStats, setScanStats] = useState(null);   // null = hidden
  const [weekStats, setWeekStats] = useState(null);   // null = hidden
  const [lastScan,  setLastScan]  = useState(null);   // ms of last geo scan, or null
  // undefined = not read yet · null = first visit · { visitedAt, snapshot }
  const [visit,     setVisit]     = useState(undefined);
  const [weekInfo,  setWeekInfo]  = useState(false);   // "סיכום שבועי" ⓘ
  // Geo events + scan stats — one fetch each per mount, cache-backed
  useEffect(() => {
    let alive = true;
    fetchGeoEvents()
      .then(evts => { if (alive) setGeoEvents(evts); })
      .catch(() => { if (alive) setGeoEvents([]); });
    fetchLastScan()
      .then(ts => { if (alive) setLastScan(ts); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    if (geoEvents === null) return;
    fetchScanStats(portfolio, watchlist, { hours: 24 })
      .then(s => { if (alive) setScanStats(s); })
      .catch(() => { if (alive) setScanStats(null); });
    fetchScanStats(portfolio, watchlist, { hours: 24 * 7 })
      .then(s => { if (alive) setWeekStats(s); })
      .catch(() => { if (alive) setWeekStats(null); });
    return () => { alive = false; };
    // re-evaluate when holdings change so the "relevant" count stays honest
  }, [geoEvents, portfolio.map(p => p.symbol).join(","), watchlist.map(w => w.symbol).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // This week's earnings for the user's symbols — today's slice renders in the
  // collapsed header, the full week in the expanded body.
  useEffect(() => {
    let alive = true;
    const symbols = [...portfolio.map(p => p.symbol), ...watchlist.map(w => w.symbol)].filter(Boolean);
    fetchEarningsCalendar(symbols, { days: AHEAD_DAYS })
      .then(rows => { if (alive) setWeekRows(rows.filter(r => r.isUserHolding)); })
      .catch(() => { if (alive) setWeekRows([]); });
    return () => { alive = false; };
  }, [portfolio.map(p => p.symbol).join(","), watchlist.map(w => w.symbol).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayISO = new Date().toISOString().slice(0, 10);

  // The market's week · macro releases (FRED) and holidays (Finnhub), the
  // same events as the calendar (sprint 13, 2.1). [] when neither answered.
  const [marketWeek, setMarketWeek] = useState(null);
  useEffect(() => {
    let alive = true;
    const to = new Date(Date.now() + AHEAD_DAYS * 86400000).toISOString().slice(0, 10);
    fetchMarketWeek({ from: todayISO, to })
      .then(rows => { if (alive) setMarketWeek(rows); })
      .catch(() => { if (alive) setMarketWeek([]); });
    return () => { alive = false; };
  }, [todayISO]);


  // ── "מה השתנה מאז הביקור הקודם" ───────────────────────────────────────────
  // Read the marker once, compare against it, and only then advance it — and
  // only if it has aged (lib/homeVisit.js). Reading and writing in the same
  // pass is what would make the screen compare the portfolio against itself.
  useEffect(() => {
    let alive = true;
    readBaseline(userId, { isGuest }).then(v => { if (alive) setVisit(v); }).catch(() => { if (alive) setVisit(null); });
    return () => { alive = false; };
  }, [userId, isGuest]);

  // Articles about the three largest holdings, for both the change list and
  // the contributions block below it — one fetch, two readers.
  const topSymbols = useMemo(() => portfolio
    .filter(p => p.quote !== "none" && p.currentPrice > 0 && p.shares > 0)
    .sort((a, b) => b.currentPrice * b.shares - a.currentPrice * a.shares)
    .slice(0, 3).map(p => p.symbol), [portfolio]);
  const holdingNames = useMemo(
    () => Object.fromEntries(portfolio.map(p => [p.symbol, p.name && p.name !== p.symbol ? p.name : ""])),
    [portfolio]);
  const { news: changeNews, titles: changeTitles } = useTodayNews(topSymbols, { names: holdingNames, perSymbol: 3 });

  // The next report date per held symbol, from the week feed — stored in the
  // snapshot so the NEXT visit can say the report was published.
  const nextReports = useMemo(() => {
    const out = {};
    // Seven days, as before the screen looked two weeks ahead: "מה השתנה"
    // says a report was published once its stored date has passed, and a
    // date further out is more often an estimate that moves (stage 3 review).
    const horizon = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    for (const r of (weekRows || [])) if (r.date <= horizon && (!out[r.symbol] || r.date < out[r.symbol])) out[r.symbol] = r.date;
    return out;
  }, [weekRows]);

  // Whether every holding has answered (portfolioReady, nothing pending).
  const settled = portfolioReady && !portfolio.some(p => p.quote === "pending");

  // Computed only once every price is in (sprint 14, 3.1): a weight measured
  // against a buy-price placeholder is a number no other screen shows.
  const changes = useMemo(() => {
    if (visit === undefined || visit === null || !settled) return null;
    return computeChanges({
      portfolio, snapshot: visit.snapshot, geoEvents: geoEvents || [],
      news: changeNews, since: visit.visitedAt,
    });
  }, [visit, portfolio, geoEvents, changeNews, settled]);

  // Whether the previous snapshot can be compared against at all. Only judged
  // once every holding has answered · before that the portfolio itself is not
  // settled, and "holdings changed" would be a false reason.
  const fit = useMemo(
    () => (visit && settled ? checkSnapshot(visit.snapshot, portfolio) : { ok: true }),
    [visit, settled, portfolio]);

  // Advance the marker once the screen has everything it compared against, so
  // a visit that failed to load does not overwrite a good snapshot.
  const marked = useRef(false);
  useEffect(() => {
    if (marked.current || visit === undefined || !portfolioReady) return;
    if (portfolio.length && !portfolio.some(p => p.currentPrice > 0)) return;
    // Every holding has answered: a pending one still shows its buy price.
    if (portfolio.some(p => p.quote === "pending")) return;
    marked.current = true;
    // A snapshot that failed the check is replaced now, not after the usual
    // 45 minutes · so the next visit has something true to compare against.
    const previous = visit && checkSnapshot(visit.snapshot, portfolio).ok ? visit : null;
    markVisit(userId, buildSnapshot(portfolio, nextReports), previous, { isGuest }).catch(() => {});
  }, [visit, portfolioReady, portfolio, nextReports, userId, isGuest]);

  // ── Geo card: how many scanned events overlap this portfolio ───────────────
  // The card reports a count of findings and nothing else. It deliberately
  // makes no claim about the state of the world — "quiet" would be an
  // assessment that a later event could falsify; "no updates" is a fact about
  // what the scan returned.
  const geoCard = useMemo(() => {
    if (!Array.isArray(geoEvents)) return { state: "loading" };
    let count = 0;
    // Most recent match at each overlap level, documents and articles apart:
    // an official document outranks a news article at the same level.
    let hot = null, geo = null, hotDoc = null, geoDoc = null;
    for (const ev of geoEvents) {
      // Only real geopolitical events count — a 'none' row is a scanned
      // article the classifier judged non-geopolitical, so it must never show
      // as intel even if it happens to name a held ticker/sector.
      if (!ev.category || ev.category === "none") continue;
      const o = computeOverlap(ev, portfolio, watchlist);
      if (o.level !== "hot" && o.level !== "geo") continue;
      count += 1;
      // Rows arrive newest-first, so the first match at a level is its latest.
      if (isOfficial(ev)) {
        if (o.level === "hot" && !hotDoc) hotDoc = ev;
        else if (o.level === "geo" && !geoDoc) geoDoc = ev;
      } else {
        if (o.level === "hot" && !hot) hot = ev;
        else if (o.level === "geo" && !geo) geo = ev;
      }
    }
    if (count === 0) return { state: "quiet" };
    // One headline under the count: the event itself, verbatim from the
    // source. Direct ticker overlap outranks sector-only overlap, and within a
    // level the document outranks the article about it.
    const top = hotDoc || hot || geoDoc || geo;
    // Guarded, not raw: the count stands on its own, so when the latest event's
    // title is an opinion column the line is simply omitted.
    return { state: "updates", count, headline: reportedHeadline(top) || "", official: isOfficial(top), at: top.published_at || top.created_at || null };
  }, [geoEvents, portfolio, watchlist]);

  // Loading while nothing has answered yet. A holding Finnhub answered with
  // "no quote" is an answer — a portfolio of only those is not loading.
  const portfolioLoading = !portfolioReady
    || (portfolio.length > 0 && !portfolio.some(s => s.prevClose > 0) && portfolio.some(s => s.quote !== "none"));

  // Switch to the market intelligence page (the geopolitical view moved there
  // in sprint 20, stage 3) and hand it the request: open this insight when a
  // source_url comes along. App owns the request and the page reads it on
  // mount, so it does not matter which of the two exists first — a CustomEvent fired here used to be dispatched before NewsTab was
  // mounted, and the insight was simply lost.
  //
  // Some callers wire this straight to onClick, so the argument may be a DOM
  // event — only a string counts as a target, anything else opens the list.
  const openGeoIntel = (sourceUrl) => {
    // On the market intelligence page since sprint 20, stage 3.
    onNavigate?.("ai", { geoFocus: typeof sourceUrl === "string" ? sourceUrl : null });
  };

  // Full-width rows (greeting, scan line, disclaimer) span both grid columns
  const fullRow = isDesktop ? { gridColumn: "1 / -1" } : null;

  // ─── Three questions, in one order ────────────────────────────────────────
  // The screen answers, in this order: how much do I have · what changed
  // since I was last here · what is coming this week. Everything else is a
  // secondary section underneath (David, 2026-09-24). `displayStyle` sets
  // density only — an order that answers questions cannot be shuffled.
  const gap = displayStyle === "growth" ? 14 : displayStyle === "conservative" ? 22 : 18;

  // "במעקב" (David, 07/10/2026): at most WATCH_TOP symbols, the largest daily
  // moves by absolute value; a symbol without a move goes last. The whole
  // list is on the portfolio screen, behind "כל רשימת המעקב".
  const watchTop = useMemo(() => [...watchlist]
    .sort((a, b) => (Number.isFinite(b.change) && b.currentPrice > 0 ? Math.abs(b.change) : -1)
                  - (Number.isFinite(a.change) && a.currentPrice > 0 ? Math.abs(a.change) : -1))
    .slice(0, WATCH_TOP), [watchlist]);

  // ── Question 1: how much do I have ─────────────────────────────────────────
  // The one panel on this screen: the value and the day's move, each with its
  // shekels (sprint 15, 2.1). The move since purchase is on the portfolio
  // screen, with its rate.
  const money = useMemo(() => {
    const quoted = portfolio.filter(p => p.quote !== "none" && p.currentPrice > 0);
    const value  = quoted.reduce((a, p) => a + p.currentPrice * p.shares, 0);
    const withPrev = quoted.filter(p => p.prevClose > 0);
    const dayNow  = withPrev.reduce((a, p) => a + p.currentPrice * p.shares, 0);
    const dayPrev = withPrev.reduce((a, p) => a + p.prevClose    * p.shares, 0);
    return {
      value,
      unquoted: portfolio.length - quoted.length,
      day:   dayPrev > 0 ? { dollar: dayNow - dayPrev, pct: ((dayNow - dayPrev) / dayPrev) * 100 } : null,
    };
  }, [portfolio]);

  // ── The brief (sprint 19, stage 2) ────────────────────────────────────────
  // The day's contributions, the S&P 500 of the same session, and · in the
  // pre-market and after the close · the extended-hours quotes (Yahoo). The
  // extended figures are judged against the clock on every render: the hook
  // re-renders each minute, so a quote that ages past 30 minutes drops out.
  const movers = useMemo(() => topMovers(portfolio, 3), [portfolio]);
  // Read again every ten minutes (the client cache's own window): the
  // sentence says "S&P 500 עד עכשיו" beside a live portfolio (stage 2 review).
  const [marketData, setMarketData] = useState(undefined);
  useEffect(() => {
    let alive = true;
    const load = () => fetchMarketData().then(md => { if (alive) setMarketData(md || null); }).catch(() => { if (alive) setMarketData(prev => prev ?? null); });
    load();
    const id = setInterval(load, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const spIndex = marketData?.sp500 ? indexForSession(marketData.sp500, market) : null;
  const extSymbols = useMemo(() => portfolio.filter(p => p.quote !== "none" && p.shares > 0).map(p => p.symbol), [portfolio]);
  const extData = useExtendedHours(extSymbols, market.state);
  const extNow = new Date();
  const extended = extData?.quotes ? {
    quotes: extData.quotes,
    estimate: estimateExtended(portfolio, extData.quotes, extNow),
    future: futureFor(extData.quotes["ES=F"], extNow),
    nasdaqFuture: futureFor(extData.quotes["NQ=F"], extNow),
  } : null;

  const heldSet = useMemo(() => new Set(portfolio.map(p => p.symbol)), [portfolio]);
  // Each holding's share of the portfolio's value, for the reports in
  // "בשבועיים הקרובים" ("בתיק שלך · משקל 21.0%").
  // Only once every price is in: a pending holding still carries its buy
  // price, and a weight against it is a number no other screen shows.
  const weights = useMemo(() => {
    const out = {};
    if (!settled || !(money.value > 0)) return out;
    for (const p of portfolio) if (p.quote !== "none" && p.currentPrice > 0 && p.shares > 0) out[p.symbol] = (out[p.symbol] || 0) + (p.currentPrice * p.shares / money.value) * 100;
    return out;
  }, [portfolio, money.value, settled]);

  // ── Question 3: what is coming this week ───────────────────────────────────
  // Holdings only, from the feed already loaded, with the days counted.
  // Holdings' reports and the market's own week (macro, holidays), by date.
  const weekAhead = useMemo(() => {
    // The holdings' reports do not wait for the market's week: its rows join
    // when they arrive (up to the 12s fetch timeout).
    if (!Array.isArray(weekRows)) return null;
    const reports = weekRows.map(r => ({ ...r, kind: "report", key: `r-${r.symbol}-${r.date}` }));
    const marketRows = (marketWeek || []).map(e => ({ date: e.date, kind: e.type, kindLabel: e.kindLabel, title: e.title, key: e.id }));
    return [...reports, ...marketRows]
      .filter(r => r.date >= todayISO)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => ({ ...r, when: daysUntilText(r.date) }))
      .filter(r => r.when);
  }, [weekRows, marketWeek, todayISO]);

  const cards = {
    geo: (
      <div
        key="geo"
        className="si-card-press"
        onClick={openGeoIntel}
        style={{ ...CARD, padding: PAD, cursor: "pointer", position: "relative" }}
      >
        {geoCard.state === "updates" ? (
          <>
                        {/* A count of what the scan found — no severity label, no call to
                action, no alert colouring. The events themselves are one tap
                away in the intelligence view. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Icon name="globe" size={17} color={UI.text.muted} style={{ flexShrink: 0 }}/>
              <span style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.5, flex: 1, minWidth: 0 }}>
                {geoCard.count === 1
                  ? "עדכון אחד חדש"
                  : <><LTR>{geoCard.count}</LTR> עדכונים חדשים</>}
              </span>
            </div>
            {/* The latest matching event, as its source phrased it — a fact,
                not a reading of it. One line; the rest is one tap away. */}
            {geoCard.headline && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, minWidth: 0 }}>
                <OfficialBadge official={!!geoCard.official}/>
                <div
                  dir={dirOf(geoCard.headline)}
                  style={{
                    fontSize: 13.5, color: UI.text.muted, lineHeight: 1.55, flex: 1, minWidth: 0,
                    unicodeBidi: "isolate",
                    display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                  {geoCard.headline}
                </div>
                {publishedNote(geoCard.headline, geoCard.at) && (
                  <span style={{ fontSize: 11.5, color: UI.text.muted, flexShrink: 0 }}>{publishedNote(geoCard.headline, geoCard.at)}</span>
                )}
              </div>
            )}
            {lastScan && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: UI.text.muted, opacity: .75, marginTop: 8 }}>
                <ScanStatusDot lastScan={lastScan}/>
                <span>נסרק לאחרונה · {timeAgoMs(lastScan)}</span>
              </div>
            )}
          </>
        ) : null /* "quiet" is the one-line row in the secondary column */}
      </div>
    ),


  };

  // 5 · אירועים קרובים and במעקב (sprint 18, stage 3). Placed by the layout
  // below: main column on a phone, side column on a desktop.
  // בשבועיים הקרובים and במעקב (sprint 19, stage 4). Placed by the layout
  // below: in the main column on a phone, at the head of the side column on
  // a desktop.
  const upcoming = (
    <>
      {/* בשבועיים הקרובים (sprint 19, stage 4) · at most three, each report
          with its weight in the portfolio; the rest in the calendar. */}
      <Section title="בשבועיים הקרובים">
        <UpcomingCard rows={weekAhead} held={heldSet} weights={weights} cardStyle={{ ...CARD, padding: PAD }}
          actionStyle={{ ...ACTION_BTN, marginTop: 4 }} onOpenCalendar={() => onNavigate?.("calendar")}/>
      </Section>
    </>
  );
  const watchSection = (
    <>
      {/* במעקב (sprint 14, 4.5) · the watchlist, one row, with each symbol's
          move on the last trading day. Only when the list is not empty. */}
      {/* Sprint 19, stage 4: hidden with nothing to show · an empty list, or
          one where no symbol has a current quote. */}
      {watchlist.some(w => Number.isFinite(w.change) && w.currentPrice > 0) && (
        <Section title="במעקב">
          <div style={{ ...CARD, padding: PAD }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 18px" }}>
              {watchTop.map(w => (
                <button key={w.symbol} onClick={() => window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol: w.symbol } }))}
                  style={{ background: "transparent", border: "none", padding: "6px 0", minHeight: 44, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                  <LTR style={{ fontWeight: 800, fontSize: 14.5, color: UI.text.primary }}>{w.symbol}</LTR>
                  {Number.isFinite(w.change) && w.currentPrice > 0
                    ? <LTR className="si-num" style={{ fontSize: 13.5, fontWeight: 700, color: shownDir(w.change) > 0 ? UI.gain : shownDir(w.change) < 0 ? UI.loss : UI.text.secondary }}>{fmtPct(w.change)}</LTR>
                    : <span style={{ fontSize: 12.5, color: UI.text.muted }}>אין ציטוט עדכני</span>}
                </button>
              ))}
            </div>
            {watchlist.length > watchTop.length && (
              <button onClick={() => { requestScroll("watchlist"); onNavigate?.("portfolio"); }} style={{ ...ACTION_BTN, marginTop: 4 }}>
                כל רשימת המעקב · <LTR className="si-num">{watchlist.length}</LTR><span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span>
              </button>
            )}
            <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 6 }}>
              {watchlist.length > watchTop.length ? "לפי גודל השינוי היומי, למעלה או למטה · " : null}
              שינוי מול סגירת המסחר הקודמת · מקור: <LTR>Finnhub</LTR>
            </div>
          </div>
        </Section>
      )}
    </>
  );

  // The secondary sections, in one place: the world around the portfolio, the
  // AI reading of it, and what the scan did this week. On a phone they follow
  // the three questions; on a desktop they sit in the side column.
  //
  // Sprint 18, stage 3: a section with nothing to report is one line or
  // nothing. The geopolitical card with no event is a single row with its
  // name in it, and the weekly counts with no event are not shown at all ·
  // that row already says when the scanner last ran. The concept of the week
  // is always last: on a phone after these sections, on a desktop at the
  // foot of the main column (the side column ends high on the page).
  const weekEmpty = [weekStats, scanStats].filter(Boolean).every(s => s.events === 0);
  const secondary = (
    <>
      {isDesktop && upcoming}
      {isDesktop && watchSection}
      {/* מודיעין גיאופוליטי · only with an event that touches the portfolio
          (sprint 19, stage 4). With none, nothing: the one-line "no events"
          row is gone with the rest of the empty sections. */}
      {geoCard.state === "updates" && (
        <Section title="מודיעין גיאופוליטי" style={{ marginTop: isDesktop ? 0 : 6 }}>
          {cards.geo}
        </Section>
      )}

      {/* Renders nothing when there is no real insight. */}
      <InsightCard
        geoEvents={geoEvents}
        portfolio={portfolio}
        watchlist={watchlist}
        onOpen={openGeoIntel}
      />

      {/* What the scan did — the week's counts and today's, one section
          instead of a card and a loose line at opposite ends of the screen. */}
      {(weekStats || scanStats) && !weekEmpty && (
        // Sprint 16, 4.3: the four lines that say what each number counts are
        // behind the ⓘ beside the heading, not under the numbers, and not in
        // the heading itself (a screen reader's list of headings).
        <Section title="סיכום שבועי"
          note={<InfoTip label="מה נספר בסיכום השבועי" text="מה כל אחד משלושת המספרים סופר, ומה לא נספר" size={15} open={weekInfo} onToggle={setWeekInfo} controls="week-info"/>}>
          <div onClick={openGeoIntel} className="si-card-press" style={{ ...CARD, padding: PAD, cursor: "pointer", display: "flex", flexDirection: "column", gap: 14 }}>
            {[weekStats, scanStats].filter(Boolean).map(s => <ScanStatsRow key={s.hours} stats={s}/>)}
            {/* Always in the page, hidden while closed, so the ⓘ's
                aria-controls points at something that exists. */}
            {(
            <div role="note" id="week-info" data-week-info hidden={!weekInfo} onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: UI.text.secondary, lineHeight: 1.6, cursor: "default" }}>
              <div>סווגו בסורק: כתבות מ-<LTR>Finnhub</LTR> ומסמכים מה-<LTR>Federal Register</LTR> שעברו את הסינון הראשוני של הסורק הגיאופוליטי ונשמרו אחרי סיווג.</div>
              <div>סווגו כאירוע גיאופוליטי: מתוכן, מה שהסיווג קבע שהוא אירוע גיאופוליטי ועבר את כללי הסינון של מסך המודיעין.</div>
              <div>נוגעים לתיק: מהאירועים, אלה שהכותרת שלהם מזכירה מניה מהתיק או מרשימת המעקב.</div>
              <div style={{ marginTop: 4 }}>מסך החדשות מציג כתבות שאינן עוברות בסורק, ולכן הן אינן נספרות כאן.</div>
            </div>
            )}
          </div>
        </Section>
      )}

    </>
  );

  return (
    <div className="si-fade-in" style={{
      display: "grid",
      // Desktop: a main area and a side area, not a dashboard of equal boxes.
      gridTemplateColumns: isDesktop ? "minmax(0, 1.55fr) minmax(0, 1fr)" : "1fr",
      columnGap: 26, rowGap: gap,
      alignItems: "start",
      minHeight: "calc(100dvh - 230px)",
      alignContent: "start",
    }}>

      {/* The market's state (sprint 19, stage 2) · the first line of the
          screen. The greeting it replaces said nothing the brief does not. */}
      <MarketStateBadge status={market} style={{ padding: "2px 2px 0", ...fullRow }}/>

      {/* Holdings never asked "what draws you here" — one line, skippable. */}
      <MarkInviteBanner portfolio={portfolio} isGuest={isGuest} style={fullRow}/>

      {/* ══ The three questions ══════════════════════════════════════════════ */}
      <div style={{ display: "flex", flexDirection: "column", gap, minWidth: 0 }}>

        {/* 1 · בקצרה (sprint 19, stage 2) · replaces the value card and its
            landscape: the session in a sentence, then the value and the
            day's change under it. */}
        <BriefCard portfolio={portfolio} market={market} movers={movers} index={spIndex}
          extended={extended} money={money} cur={cur} loading={portfolioLoading} isDesktop={isDesktop}/>

        {/* מה קורה בשוק היום (sprint 18, stage 2) · one stored summary for
            every user, with the index row from the same run. */}
        <Section title="מה קורה בשוק היום">
          <MarketTodayCard cardStyle={{ ...CARD, padding: PAD }} live={marketData}
            onAllIndices={() => { requestScroll("markets:us"); onNavigate?.("portfolio"); }}
            futures={market.state === "pre" && extended ? { sp: extended.future, nq: extended.nasdaqFuture } : null}/>
        </Section>

        {/* מה הזיז את התיק (sprint 18, stage 1) · the day's move, broken down
            into each holding's share of it, in points of the portfolio. It
            replaces "התיק היום", which ranked by percent and left the reader
            to weigh each move by the holding's size. */}
        {portfolio.length > 0 && (
          <Section title="מה הזיז את התיק">
            <PortfolioMoversCard
              portfolio={portfolio}
              market={market}
              extended={extended}
              loading={portfolioLoading}
              cardStyle={{ ...CARD, padding: PAD }}
              onOpenStock={symbol => window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol } }))}
            />
          </Section>
        )}

        {/* בשבועיים הקרובים, after the movers on a phone (sprint 19, stage 4). */}
        {!isDesktop && upcoming}

        {/* מה השתנה מאז הביקור הקודם · only with something to list */}
        {changes && changes.length > 0 && (
        <Section
          title="מה השתנה מאז הביקור הקודם"
          note={visit ? `הביקור הקודם · ${dateNumeric(visit.visitedAt, { year: false })} ${String(visit.visitedAt.getHours()).padStart(2, "0")}:${String(visit.visitedAt.getMinutes()).padStart(2, "0")}` : null}
        >
          {/* Sprint 19, stage 4: the section is drawn only when there is a
              change to list · loading, a first visit and "nothing changed"
              are not shown at all. */}
          <div style={{ ...CARD, padding: "6px 22px 10px" }}>
              {/* The events and articles do not depend on the snapshot, so they
                  stay; the prices and weights that do are the part withheld. */}
              {!fit.ok && (
                <div style={{ padding: "14px 0 10px", borderBottom: `1px solid ${UI.line.hairline}` }}>
                  <div style={{ fontSize: 14, color: UI.text.primary }}>{NOT_COMPARABLE}</div>
                  <div style={{ fontSize: 12.5, color: UI.text.muted, marginTop: 2 }}>{NOT_COMPARABLE_WHY[fit.reason]} · מחירים ומשקלות לא הושוו</div>
                </div>
              )}
              {changes.map((c, i) => (
                /* A row opens the company page · the same facts, for one company. */
                <div key={c.id} role="button" tabIndex={0}
                  onClick={() => window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol: c.symbol } }))}
                  onKeyDown={e => { if (e.key === "Enter" && e.target === e.currentTarget) window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol: c.symbol } })); }}
                  style={{ padding: "14px 0", borderTop: i === 0 ? "none" : `1px solid ${UI.line.hairline}`, cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: UI.text.primary }}>{c.text}</span>
                    {c.figure && c.figureRtl ? (
                      <span dir="rtl" style={{ fontSize: 15, fontWeight: 800, color: UI.text.primary }}>מ-<LTR className="si-num">{c.from.toFixed(1)}%</LTR> ל-<LTR className="si-num">{c.to.toFixed(1)}%</LTR></span>
                    ) : c.figure && (
                      <LTR className="si-num" style={{
                        fontSize: 15, fontWeight: 800,
                        color: c.kind === "price" ? (c.move > 0 ? UI.gain : UI.loss) : UI.text.primary,
                      }}>
                        {c.kind === "report" ? dateNumeric(c.figure) : c.figure}
                        {/* What the move did to the portfolio · the number the
                            rows are ranked by (sprint 16, 4.1). */}
                        {c.kind === "price" && Number.isFinite(c.impact) && <> · <DualMoney usd={c.impact} cur={cur} size={15} signed inline color={c.move > 0 ? UI.gain : UI.loss}>
                          <Money value={c.impact} cur={cur} size={15} weight={800} signed color={c.move > 0 ? UI.gain : UI.loss} className="si-num"/>
                        </DualMoney></>}
                      </LTR>
                    )}
                  </div>
                  {/* A news row shows its headline in Hebrew only (26/10/2026):
                      until the translation is in, the row is the count. */}
                  {(() => {
                    const hl = c.kind === "news" ? (c.articleKey && changeTitles[c.articleKey]) || null : c.headline;
                    if (!hl) return null;
                    const when = publishedNote(hl, c.at);
                    return (
                      <>
                        {c.kind === "news"
                          ? <Headline text={hl} style={{ fontSize: 13.5, color: UI.text.secondary, lineHeight: 1.6, marginTop: 4 }}/>
                          : <div dir={dirOf(hl)} style={{ fontSize: 13.5, color: UI.text.secondary, lineHeight: 1.6, marginTop: 4 }}>{hl}</div>}
                        {when && <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 2 }}>{when}</div>}
                      </>
                    );
                  })()}
                  <PortfolioTie symbol={c.tie.symbol} symbols={c.tie.symbols} weight={c.tie.weight} relation={c.tie.relation} style={{ marginTop: 6 }}/>
                  <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 3 }}>מקור: {c.source}</div>
                </div>
              ))}
            </div>

        </Section>
        )}

        {/* במעקב on a phone, after the changes. */}
        {!isDesktop && watchSection}

        {/* מושג השבוע · one line (sprint 19, stage 4), the foot of the main
            column on a desktop. */}
        {ACADEMY_ENABLED && isDesktop && <ConceptOfWeekLine/>}
      </div>

      {/* ══ Secondary ═══════════════════════════════════════════════════════ */}
      <div style={{ display: "flex", flexDirection: "column", gap, minWidth: 0 }}>
        {secondary}
        {/* מושג השבוע (phone) · one line, the last thing on the screen. */}
        {ACADEMY_ENABLED && !isDesktop && <ConceptOfWeekLine/>}
      </div>

      {/* The disclaimer is the fixed bar at the foot of every screen
          (App · StatusRow), once (sprint 17). */}
    </div>
  );
}
